import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Clock, XCircle, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyles = {
  confirmed: { color: 'text-white', bg: 'bg-white/10', icon: Check },
  pending: { color: 'text-white/50', bg: 'bg-white/5', icon: Clock },
  cancelled: { color: 'text-white/30', bg: 'bg-white/3', icon: XCircle },
};

export default function AdminPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/bookings`);
      setBookings(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status`, { status });
      fetchBookings();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-white/30 transition-colors"
              data-testid="admin-back-button"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="font-heading text-2xl" data-testid="admin-title">BOOKINGS</h1>
              <p className="font-mono text-xs text-white/30">{bookings.length} total</p>
            </div>
          </div>
          <button
            onClick={fetchBookings}
            className="btn-outline text-xs flex items-center gap-2 py-3 px-5"
            data-testid="admin-refresh-button"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center py-24">
            <p className="font-mono text-xs text-white/30">Loading bookings...</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-24" data-testid="admin-empty-state">
            <p className="font-heading text-xl text-white/20 mb-2">NO BOOKINGS YET</p>
            <p className="font-mono text-xs text-white/15">Bookings will appear here when clients submit consultations.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full admin-table" data-testid="bookings-table">
              <thead>
                <tr>
                  <th className="text-left">Client</th>
                  <th className="text-left">Email</th>
                  <th className="text-left">Project</th>
                  <th className="text-left">Package</th>
                  <th className="text-left">Payment</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Date</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking, i) => {
                  const style = statusStyles[booking.status] || statusStyles.pending;
                  const StatusIcon = style.icon;
                  return (
                    <motion.tr
                      key={booking.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.05 }}
                      data-testid={`booking-row-${i}`}
                    >
                      <td className="text-white/80">
                        <div>
                          <p className="text-sm">{booking.name}</p>
                          {booking.company && <p className="text-xs text-white/20">{booking.company}</p>}
                        </div>
                      </td>
                      <td className="text-white/40 text-sm">{booking.email}</td>
                      <td className="text-white/40 text-sm">{booking.project_type}</td>
                      <td>
                        <span className="font-mono text-xs text-white/30">
                          {booking.package_id ? booking.package_id.toUpperCase() : '—'}
                        </span>
                      </td>
                      <td>
                        <span className="font-mono text-xs text-white/30">
                          {booking.payment_status || '—'}
                        </span>
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 ${style.bg} ${style.color}`}>
                          <StatusIcon size={12} />
                          {booking.status}
                        </span>
                      </td>
                      <td className="font-mono text-xs text-white/20">
                        {new Date(booking.created_at).toLocaleDateString()}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {booking.status === 'pending' && (
                            <>
                              <button
                                onClick={() => updateStatus(booking.id, 'confirmed')}
                                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-white/10 hover:border-white/30 text-white/40 hover:text-white transition-all"
                                data-testid={`confirm-booking-${i}`}
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => updateStatus(booking.id, 'cancelled')}
                                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-white/5 hover:border-white/15 text-white/20 hover:text-white/40 transition-all"
                                data-testid={`cancel-booking-${i}`}
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
